import * as React from 'react'

import MarkupToolButton, { IProps } from './MarkupToolButton'

// iOS drops the click of a keyboard-dismissing tap: fire selection on pointer-up,
// then cancel the tap so its synthetic mouse tail can't drop a stray shape.
const MobileMarkupToolButton: React.FC<IProps> = props => {
  const { onClick, disabled } = props
  const armed = React.useRef(false)
  const firedOnTouch = React.useRef(false)
  const startedHere = React.useRef(false)

  const onPointerDownCapture = (e: React.PointerEvent): void => {
    firedOnTouch.current = false
    // The tap physically began on this button — a click without this is a bleed (below).
    startedHere.current = true
    const active = document.activeElement
    armed.current =
      (e.pointerType === 'touch' || e.pointerType === 'pen') &&
      (active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        // Text markup's contenteditable — same keyboard, same swallowed click.
        (active instanceof HTMLElement && active.isContentEditable))
  }
  const onPointerUpCapture = (): void => {
    // Keyboard case only: iOS swallows this tap's click, so fire selection here.
    if (!armed.current) return
    armed.current = false
    if (disabled) return
    firedOnTouch.current = true
    onClick()
  }
  const onPointerCancelCapture = (): void => {
    // The gesture became a scroll/drag — no pointer-up or click follows, so drop the
    // per-tap state or a stale `startedHere` would let one later bleed click through.
    armed.current = false
    startedHere.current = false
    firedOnTouch.current = false
  }
  const onTouchEndCapture = (e: React.TouchEvent): void => {
    // Fired on pointer-up → cancel the tap so iOS drops its synthetic mouse tail
    // (the sheet collapsed to peek, so the tail would land on the map canvas).
    if (firedOnTouch.current && e.cancelable) e.preventDefault()
  }
  const handleClick = (e?: React.MouseEvent): void => {
    const began = startedHere.current
    startedHere.current = false
    if (firedOnTouch.current) {
      firedOnTouch.current = false
      return
    }
    // A pointerdown action elsewhere (Create bubble) can mount the toolbar mid-tap
    // under the finger — ignore its bleed click. Keyboard/AT has no tap (detail 0).
    if (!began && e?.detail !== 0) return
    onClick()
  }

  return (
    <span
      className="tw:contents"
      onPointerDownCapture={onPointerDownCapture}
      onPointerUpCapture={onPointerUpCapture}
      onPointerCancelCapture={onPointerCancelCapture}
      onTouchEndCapture={onTouchEndCapture}
    >
      <MarkupToolButton {...props} onClick={handleClick} />
    </span>
  )
}

export default MobileMarkupToolButton
